import '@sendbird/uikit-react/dist/index.css';

import SendbirdProvider from '@sendbird/uikit-react/SendbirdProvider';
import React from 'react';
import { FullScreenSpinner } from 'smoothie';

import type { ContainerComponent } from '../../common/types/Common';
import { SENDBIRD_APP_ID } from '../../config/config';
import { requestBuilder } from '../../http/RequestBuilder';
import { captureException } from '../../logger/Logger';
import type { SendbirdUser } from '../types/GuideMessaging';

const useGetGuideMessaging = requestBuilder.query<SendbirdUser>({
    endpoint: () => `/api/admin/guide-messaging/guide`,
    queryKey: () => `getGuideMessaging`,
});

export const GuideMessagingProvider: ContainerComponent = ({ children }) => {
    // Fetch the user's Sendbird credentials.
    const {
        data: sendbirdUser,
        isLoading: isLoadingGuideMessaging,
        isError: isGuideMessagingError,
    } = useGetGuideMessaging({});

    if (isLoadingGuideMessaging) {
        return <FullScreenSpinner />;
    }

    if (
        isGuideMessagingError ||
        sendbirdUser?.userId == null ||
        sendbirdUser?.accessToken == null
    ) {
        captureException(new Error('Failed to load Guide Messaging.'));
        return <>{children}</>;
    }

    const { userId, accessToken } = sendbirdUser;

    // Override default colors for Sendbird UIKit. These colors are copied from the legacy member app's tailwind config.
    const messagingColorTheme = {
        '--sendbird-light-primary-500': '#176457',
        '--sendbird-light-primary-400': '#176457',
        '--sendbird-light-primary-300': '#1E8574',
        '--sendbird-light-primary-200': '#8EC1BA',
        '--sendbird-light-primary-100': '#C7E1DC',
    };

    // Note: Intentionally excluding fields like nickname and profileUrl on the SendbirdProvider for now. If these are provided, Sendbird attempts to make a PUT request to update the user's profile, which we've disabled in our Sendbird settings, for now.
    return (
        <SendbirdProvider
            appId={SENDBIRD_APP_ID}
            userId={userId}
            accessToken={accessToken}
            colorSet={messagingColorTheme}
            sdkInitParams={{
                localCacheEnabled: false, // Disable the local cache, which uses IndexedDB. We've observed several instances of an issue where the full list of channels is not loaded for a Grove user. Clearing the IndexedDB cache resolves the issue, and its believed to be the result of IndexedDB and/or domain-level storage hitting a size limit. Disabling the cache will always pull the data from the network, which should improve data integrity at the cost of slightly slower load times. We don't see this issue on member-app since the number of channels, and thus size of data, is significantly lower.
            }}
            uikitOptions={{
                groupChannel: {
                    enableMarkdownForUserMessage: true, // Enable markdown formatting. Currently only Bold and Links are supported. See https://sendbird.com/docs/ai-chatbot/guide/v1/uikit/markdown-message.
                    enableMarkAsUnread: true, // Enable the mark as unread feature.
                },
            }}
        >
            {children}
        </SendbirdProvider>
    );
};
